<template>
  <div class="chart-box">
    <h4 style="margin-bottom: 0">{{ item.notation }}. {{ item.caption }}</h4>
    <div class="svg-container">
      <svg viewBox="0 0 36 36" class="donut">
        <circle
          v-for="(segment, i) in segments"
          :key="i"
          class="donut-segment"
          :style="{
            strokeDasharray: calculateDashArray(segment.value, 14 - i / 2),
            strokeDashoffset: calculateOffset(14 - i / 2, segments.slice(0, i)),
            stroke: segment.color,
          }"
          cx="18"
          cy="18"
          :r="14 - i / 2"
          :stroke-width="8 - i"
          fill="none"
        >
          <title>{{ segment.count }}</title>
        </circle>
      </svg>
      <div class="elem-center">
        <div class="text-center">проектов:</div>
        <div class="chart-center">{{ item.projectCount }}</div>
      </div>
    </div>
    <ul style="margin-bottom: 0" class="legend">
      <li v-for="(segment, i) in segments" :key="i">
        <span :style="{ backgroundColor: segment.color }"></span>
        {{
          `${segment.notation ? segment.notation + '.' : ''} ${segment.caption}`
        }}
      </li>
    </ul>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';

  const segmentColors = [
    '#383D4F',
    '#F2631B',
    '#53AD7F',
    '#AEB4DD',
    '#AE4AAD',
    '#222831',
    '#f05454',
    '#16a085',
  ];

  const props = defineProps({
    /** Top UDC Item with stats */
    item: {
      type: Object,
      required: true,
    },
    maxSegments: {
      type: Number,
      default: 3,
    },
  });

  const segments = ref([]);

  onMounted(() => {
    prepareSegments();
  });

  const prepareSegments = () => {
    const innerUdcList = props.item.items;
    const colors = randomizeColors();

    const other = [];
    innerUdcList.forEach((item, index) => {
      if (index + 1 < props.maxSegments) {
        segments.value.push({
          notation: item.notation,
          caption: item.caption,
          count: item.udcCount,
          value: (item.udcCount / props.item.udcCount) * 100,
          color: colors.pop(),
        });
      } else {
        other.push(item);
      }
    });

    if (other.length > 0) {
      const otherCount = other.reduce((acc, item) => acc + item.udcCount, 0);
      segments.value.push({
        notation: '',
        caption: 'Остальные...',
        count: otherCount,
        value: (otherCount / props.item.udcCount) * 100,
        color: colors.pop(),
      });
    }
  };

  /**
   * Функция вычисляет значения `stroke-dasharray` для сегмента круговой диаграммы.
   * `stroke-dasharray` определяет, какая часть окружности будет закрашена, а какая — прозрачной.
   *
   * @param {number} value - Процент сегмента (от 0 до 100), который должен быть закрашен.
   * @param {number} radius - Радиус окружности, для которой вычисляется длина сегмента.
   * @returns {string} Строка формата `"длина_сегмента длина_пробела"`, используемая в `stroke-dasharray`.
   */
  const calculateDashArray = (value, radius) => {
    // Вычисляем угол сегмента
    const angle = 2 * Math.PI * (value / 100);

    // Вычисляем длину сегмента
    const seg1 = radius * angle;

    // Вычисляем длину оставшегося (не закрашенного) сегмента
    const seg2 = (2 * Math.PI - angle) * radius;

    // Возвращаем строку формата `"длина_сегмента длина_пробела"`
    // Это нужно для корректного отображения круговой диаграммы в SVG
    return `${seg1} ${seg2}`;
  };

  /**
   * Функция вычисляет смещение (`strokeDashoffset`) для сегмента диаграммы.
   * Смещение нужно, чтобы каждый сегмент начинался сразу после предыдущего.
   *
   * @param {number} radius - Радиус окружности сегмента.
   * @param {Array} previousSegments - Массив предыдущих сегментов (включает их значения).
   * @returns {number} Смещение по окружности, чтобы сегмент встал в правильное место.
   */
  const calculateOffset = (radius, previousSegments) => {
    // Вычисляем угол предыдущих сегментов (в радианах)
    const prevAngle = previousSegments.reduce((acc, seg) => {
      return acc + 2 * Math.PI * (seg.value / 100);
    }, 0);

    // Возвращаем оставшееся расстояние по окружности, чтобы сместить новый сегмент
    return radius * (2 * Math.PI - prevAngle);
  };

  /**
   * Создает массив случайных цветов из списка цветов на входе
   */
  const randomizeColors = () => {
    const colors = [...segmentColors];
    const shift = props.item.udcClassId % segmentColors.length;
    colors.slice(-shift).concat(colors.slice(0, -shift));

    for (let i = colors.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      // Swap
      [colors[i], colors[j]] = [colors[j], colors[i]];
    }
    return colors;
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors.scss' as *;

  h4 {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 20px;
    color: #333;
  }

  .chart-box {
    display: flex;
    flex-direction: column;
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    height: auto;
  }

  .svg-container {
    position: relative;
    width: 225px;
    height: 225px;
    margin: 0 0 0 auto;
  }

  .donut {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  .donut-segment {
    fill: none;
    stroke-linecap: butt;
    transform-origin: center;
  }

  .elem-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .text-center {
    font-size: 14px;
    color: #718096;
  }

  .chart-center {
    font-size: 35px;
    font-weight: bold;
    color: #2d3346;
  }

  .legend {
    align-self: flex-start;
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    margin-top: auto;
  }

  .legend li {
    display: flex;
    align-items: flex-start;
    font-family: Nunito Sans, sans-serif;
    font-weight: 400;
    font-size: 11px;
    line-height: 100%;
    vertical-align: middle;
    color: $color-bg-primary-black-100;
    margin-bottom: 10px;
  }

  .legend span {
    display: inline-block;
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 10px;
  }
</style>
